/**
 * DisplayTab - Onglet "Affichage" du rôle.
 *
 * Permet de modifier le libellé, le nom technique, la couleur, la description
 * et le flag "rôle par défaut". Le bouton Supprimer est masqué pour les rôles
 * système.
 */

import React, { useState, useEffect } from 'react';
import { TrashIcon, CheckIcon, CloseIcon } from '../../icons';
import {
  RoleDetail,
  COLOR_PALETTE,
  DEFAULT_ROLE_COLOR,
  API_BASE,
  authHeaders,
  ROLE_NAME_REGEX
} from './types';
import DeleteRoleModal from './DeleteRoleModal';

interface DisplayTabProps {
  role: RoleDetail;
  onSaved: () => void;
  onDeleted: () => void;
}

interface FormState {
  display_name: string;
  name: string;
  color: string;
  description: string;
  is_default: boolean;
}

function buildState(role: RoleDetail): FormState {
  return {
    display_name: role.display_name || '',
    name: role.name || '',
    color: role.color || DEFAULT_ROLE_COLOR,
    description: role.description || '',
    is_default: !!role.is_default
  };
}

function DisplayTab({ role, onSaved, onDeleted }: DisplayTabProps) {
  const [form, setForm] = useState<FormState>(() => buildState(role));
  const [initial, setInitial] = useState<FormState>(() => buildState(role));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  useEffect(() => {
    const next = buildState(role);
    setForm(next);
    setInitial(next);
    setError(null);
    setSuccess(null);
  }, [role]);

  const isDirty =
    form.display_name !== initial.display_name ||
    form.name !== initial.name ||
    form.color !== initial.color ||
    form.description !== initial.description ||
    form.is_default !== initial.is_default;

  const isCustomColor = !COLOR_PALETTE.includes(form.color);

  function handleColorPick(color: string) {
    setForm(prev => ({ ...prev, color }));
  }

  function handleNameChange(value: string) {
    // Filtre la saisie pour ne garder que les caractères autorisés
    const cleaned = value.toLowerCase().replace(/[^a-z0-9_]/g, '');
    setForm(prev => ({ ...prev, name: cleaned }));
  }

  async function handleSave(e: React.FormEvent) {
    e.preventDefault();
    if (!isDirty) return;

    if (!form.display_name.trim()) {
      setError('Le libellé est obligatoire.');
      return;
    }
    if (!role.is_system && !ROLE_NAME_REGEX.test(form.name)) {
      setError('Le nom technique doit commencer par une lettre minuscule (a-z) et ne contenir que des lettres minuscules, chiffres ou underscores.');
      return;
    }

    setSaving(true);
    setError(null);
    setSuccess(null);

    try {
      console.log(`[DisplayTab] PUT /api/roles/${role.id}`);
      const body: Record<string, unknown> = {
        display_name: form.display_name.trim(),
        color: form.color,
        description: form.description.trim() || null,
        is_default: form.is_default
      };
      if (!role.is_system) {
        body.name = form.name;
      }
      const res = await fetch(`${API_BASE}/api/roles/${role.id}`, {
        method: 'PUT',
        headers: authHeaders(),
        body: JSON.stringify(body)
      });
      const json = await res.json();
      if (!res.ok || !json.success) {
        throw new Error(json?.error?.message || `Erreur HTTP ${res.status}`);
      }
      console.log('[DisplayTab] Rôle mis à jour');
      setSuccess('Rôle enregistré.');
      setTimeout(() => setSuccess(null), 3000);
      onSaved();
    } catch (err) {
      console.error('[DisplayTab] Erreur save:', err);
      setError(err instanceof Error ? err.message : 'Erreur lors de l\'enregistrement');
    } finally {
      setSaving(false);
    }
  }

  function handleCancel() {
    setForm(initial);
    setError(null);
    setSuccess(null);
  }

  return (
    <form onSubmit={handleSave} style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
      {/* Libellé */}
      <div>
        <label style={labelStyle}>
          Libellé <span style={{ color: '#dc2626' }}>*</span>
        </label>
        <input
          type="text"
          value={form.display_name}
          onChange={e => setForm(prev => ({ ...prev, display_name: e.target.value }))}
          placeholder="Ex : Responsable de site"
          required
          style={inputStyle}
        />
      </div>

      {/* Nom technique */}
      <div>
        <label style={labelStyle}>
          Nom technique
          {role.is_system && (
            <span style={{
              fontSize: '11px',
              fontWeight: 'normal',
              color: '#94a3b8',
              marginLeft: '8px'
            }}>
              (non modifiable, rôle système)
            </span>
          )}
        </label>
        <input
          type="text"
          value={form.name}
          readOnly={role.is_system}
          onChange={e => handleNameChange(e.target.value)}
          placeholder="ex : responsable_site"
          style={{
            ...inputStyle,
            backgroundColor: role.is_system ? '#f1f5f9' : 'white',
            cursor: role.is_system ? 'not-allowed' : 'text',
            fontFamily: 'monospace',
            fontSize: '13px'
          }}
        />
        {!role.is_system && (
          <div style={hintStyle}>
            Lettres minuscules, chiffres et underscores uniquement. Doit commencer par une lettre.
          </div>
        )}
      </div>

      {/* Couleur */}
      <div>
        <label style={labelStyle}>Couleur</label>
        <div style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: '8px',
          marginBottom: '8px'
        }}>
          {COLOR_PALETTE.map(c => {
            const isSelected = form.color === c;
            return (
              <button
                key={c}
                type="button"
                onClick={() => handleColorPick(c)}
                style={{
                  width: '32px',
                  height: '32px',
                  borderRadius: '50%',
                  backgroundColor: c,
                  border: isSelected ? '3px solid #3b82f6' : '2px solid #e2e8f0',
                  cursor: 'pointer',
                  padding: 0,
                  outline: 'none',
                  boxShadow: isSelected ? '0 0 0 2px white inset' : 'none'
                }}
                title={c}
              />
            );
          })}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <span style={{ fontSize: '12px', color: '#64748b' }}>Hex :</span>
          <input
            type="text"
            value={form.color}
            onChange={e => setForm(prev => ({ ...prev, color: e.target.value }))}
            placeholder="#3b82f6"
            style={{
              ...inputStyle,
              width: '120px',
              fontFamily: 'monospace',
              fontSize: '12px',
              padding: '6px 10px'
            }}
          />
          {isCustomColor && /^#[0-9a-f]{6}$/i.test(form.color) && (
            <span style={{ fontSize: '11px', color: '#94a3b8' }}>(personnalisée)</span>
          )}
        </div>
      </div>

      {/* Description */}
      <div>
        <label style={labelStyle}>Description</label>
        <textarea
          value={form.description}
          onChange={e => setForm(prev => ({ ...prev, description: e.target.value }))}
          placeholder="Description optionnelle du rôle..."
          rows={3}
          style={{
            ...inputStyle,
            resize: 'vertical',
            minHeight: '64px',
            fontFamily: 'inherit'
          }}
        />
      </div>

      {/* Rôle par défaut */}
      <div>
        <label style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          cursor: 'pointer',
          fontSize: '14px',
          color: '#374151'
        }}>
          <input
            type="checkbox"
            checked={form.is_default}
            onChange={e => setForm(prev => ({ ...prev, is_default: e.target.checked }))}
            style={{ cursor: 'pointer' }}
          />
          Rôle attribué par défaut aux nouveaux utilisateurs
        </label>
        <div style={hintStyle}>
          Un seul rôle peut être marqué comme rôle par défaut. Cocher cette case décochera l'autre.
        </div>
      </div>

      {/* Messages */}
      {error && (
        <div style={errorBoxStyle}>
          <CloseIcon size={14} />
          {error}
        </div>
      )}
      {success && (
        <div style={successBoxStyle}>
          <CheckIcon size={14} />
          {success}
        </div>
      )}

      {/* Boutons */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        paddingTop: '12px',
        borderTop: '1px solid #e2e8f0'
      }}>
        <button
          type="button"
          onClick={handleCancel}
          disabled={!isDirty || saving}
          style={{
            padding: '8px 16px',
            backgroundColor: '#f1f5f9',
            color: '#475569',
            border: '1px solid #e2e8f0',
            borderRadius: '6px',
            fontSize: '13px',
            cursor: !isDirty || saving ? 'not-allowed' : 'pointer',
            opacity: !isDirty || saving ? 0.5 : 1
          }}
        >
          Annuler
        </button>
        <button
          type="submit"
          disabled={!isDirty || saving}
          style={{
            padding: '8px 16px',
            backgroundColor: !isDirty || saving ? '#93c5fd' : '#3b82f6',
            color: 'white',
            border: 'none',
            borderRadius: '6px',
            fontSize: '13px',
            fontWeight: 500,
            cursor: !isDirty || saving ? 'not-allowed' : 'pointer'
          }}
        >
          {saving ? 'Enregistrement...' : 'Enregistrer'}
        </button>

        {!role.is_system && role.name !== 'technician' && (
          <button
            type="button"
            onClick={() => setShowDeleteModal(true)}
            disabled={saving}
            style={{
              marginLeft: 'auto',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              padding: '8px 16px',
              backgroundColor: '#fef2f2',
              color: '#dc2626',
              border: '1px solid #fca5a5',
              borderRadius: '6px',
              fontSize: '13px',
              fontWeight: 500,
              cursor: saving ? 'not-allowed' : 'pointer'
            }}
          >
            <TrashIcon size={14} />
            Supprimer le rôle
          </button>
        )}
      </div>

      {showDeleteModal && (
        <DeleteRoleModal
          role={role}
          onClose={() => setShowDeleteModal(false)}
          onConfirmed={() => {
            setShowDeleteModal(false);
            onDeleted();
          }}
        />
      )}
    </form>
  );
}

const labelStyle: React.CSSProperties = {
  display: 'block',
  fontSize: '13px',
  fontWeight: 500,
  color: '#374151',
  marginBottom: '6px'
};

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '8px 12px',
  border: '1px solid #d1d5db',
  borderRadius: '6px',
  fontSize: '14px',
  outline: 'none',
  boxSizing: 'border-box'
};

const hintStyle: React.CSSProperties = {
  fontSize: '12px',
  color: '#94a3b8',
  marginTop: '4px'
};

const errorBoxStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  padding: '10px 12px',
  backgroundColor: '#fef2f2',
  color: '#991b1b',
  border: '1px solid #fca5a5',
  borderRadius: '6px',
  fontSize: '13px'
};

const successBoxStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  padding: '10px 12px',
  backgroundColor: '#dcfce7',
  color: '#166534',
  border: '1px solid #86efac',
  borderRadius: '6px',
  fontSize: '13px'
};

export default DisplayTab;
